import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { FileSearch, Loader2, Sparkles, CheckCircle, Mail, FileText, RefreshCw, Plus, X, Save, Calculator, Briefcase } from 'lucide-react';

const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE') : '');

function ManualBroker({ deal, onSaved }) {
  const [val, setVal] = useState(deal.manual_broker || '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(deal.manual_broker || ''); }, [deal.manual_broker]);

  const save = async () => {
    setSaving(true);
    try {
      await api.saveAcManualBroker(deal.project_id, val.trim());
      toast.success(val.trim() ? `Broker gespeichert: ${val.trim()}` : 'Manueller Broker entfernt');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setSaving(false);
  };

  return (
    <div className="flex items-center gap-1.5 border border-border rounded-sm px-2.5 py-1.5" data-testid={`ac-manual-broker-${deal.project_id}`}>
      <Briefcase className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <span className="text-xs text-muted-foreground shrink-0">Broker manuell:</span>
      <input value={val} onChange={e => setVal(e.target.value)} placeholder="z.B. Deloitte M&A — Max Mustermann"
        data-testid={`ac-manual-broker-input-${deal.project_id}`}
        className="flex-1 min-w-[140px] h-7 bg-secondary border border-border rounded-sm px-2 text-xs text-foreground focus:outline-none focus:border-amber-500" />
      <button onClick={save} disabled={saving || val.trim() === (deal.manual_broker || '')}
        data-testid={`ac-manual-broker-save-${deal.project_id}`}
        className="h-7 px-2.5 text-[11px] text-emerald-200 border border-emerald-500/40 hover:bg-emerald-500/10 rounded-sm flex items-center gap-1 disabled:opacity-40 shrink-0">
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Speichern
      </button>
    </div>
  );
}

function ManualFinancials({ deal, onSaved }) {
  const [open, setOpen] = useState((deal.manual_financials || []).length > 0);
  const [rows, setRows] = useState(() =>
    (deal.manual_financials || []).map(y => ({ year: y.year || '', revenue_m: y.revenue_m ?? '', ebitda_m: y.ebitda_m ?? '' })));
  const [saving, setSaving] = useState(false);

  const setCell = (i, key, val) => setRows(prev => prev.map((r, j) => (j === i ? { ...r, [key]: val } : r)));
  const save = async () => {
    setSaving(true);
    try {
      const history = rows
        .filter(r => r.year.trim() && (r.revenue_m !== '' || r.ebitda_m !== ''))
        .map(r => ({ year: r.year.trim(),
                     revenue_m: r.revenue_m === '' ? null : parseFloat(String(r.revenue_m).replace(',', '.')),
                     ebitda_m: r.ebitda_m === '' ? null : parseFloat(String(r.ebitda_m).replace(',', '.')) }));
      await api.saveAcManualFinancials(deal.project_id, history);
      toast.success(`${deal.project_name}: ${history.length} Jahre gespeichert — fließen ins Chart der AC-Mail`);
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setSaving(false);
  };

  return (
    <div className="border border-border rounded-sm" data-testid={`ac-manual-fin-${deal.project_id}`}>
      <button onClick={() => setOpen(v => !v)} data-testid={`ac-manual-fin-toggle-${deal.project_id}`}
        className="w-full px-2.5 py-1.5 flex items-center gap-1.5 text-left text-xs text-muted-foreground hover:text-foreground transition-colors">
        <Calculator className="w-3.5 h-3.5" />
        Historicals manuell erfassen (z.B. aus Telefonaten)
        {(deal.manual_financials || []).length > 0 && (
          <span className="text-[10px] font-mono text-emerald-300">{deal.manual_financials.length} Jahre erfasst</span>
        )}
        <span className="ml-auto text-[10px]">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="px-2.5 pb-2.5 space-y-1.5">
          <div className="grid grid-cols-[80px_1fr_1fr_24px] gap-1.5 text-[10px] text-muted-foreground">
            <span>Jahr</span><span>Umsatz €m</span><span>EBITDA €m</span><span />
          </div>
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[80px_1fr_1fr_24px] gap-1.5">
              <input value={r.year} onChange={e => setCell(i, 'year', e.target.value)} placeholder="2024"
                data-testid={`ac-manual-fin-year-${deal.project_id}-${i}`}
                className="h-7 bg-secondary border border-border rounded-sm px-2 text-xs text-foreground focus:outline-none focus:border-amber-500" />
              <input value={r.revenue_m} onChange={e => setCell(i, 'revenue_m', e.target.value)} placeholder="12,4"
                data-testid={`ac-manual-fin-rev-${deal.project_id}-${i}`}
                className="h-7 bg-secondary border border-border rounded-sm px-2 text-xs text-foreground focus:outline-none focus:border-amber-500" />
              <input value={r.ebitda_m} onChange={e => setCell(i, 'ebitda_m', e.target.value)} placeholder="1,8"
                data-testid={`ac-manual-fin-ebitda-${deal.project_id}-${i}`}
                className="h-7 bg-secondary border border-border rounded-sm px-2 text-xs text-foreground focus:outline-none focus:border-amber-500" />
              <button onClick={() => setRows(prev => prev.filter((_, j) => j !== i))}
                className="text-muted-foreground/50 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <div className="flex items-center gap-2 pt-0.5">
            <button onClick={() => setRows(prev => [...prev, { year: '', revenue_m: '', ebitda_m: '' }])}
              data-testid={`ac-manual-fin-add-${deal.project_id}`}
              className="h-7 px-2 text-[11px] text-muted-foreground hover:text-foreground border border-border rounded-sm flex items-center gap-1">
              <Plus className="w-3 h-3" /> Jahr
            </button>
            <button onClick={save} disabled={saving} data-testid={`ac-manual-fin-save-${deal.project_id}`}
              className="h-7 px-2.5 text-[11px] text-emerald-200 border border-emerald-500/40 hover:bg-emerald-500/10 rounded-sm flex items-center gap-1 disabled:opacity-40">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Speichern
            </button>
            <span className="text-[10px] text-muted-foreground">Ab 2 Jahren als Balken-Chart in der AC-Mail; gleiche Jahre überschreiben Teaser-Werte.</span>
          </div>
        </div>
      )}
    </div>
  );
}

function TeaserSummary({ teaser, deal }) {
  const d = teaser.data || {};
  const hist = Array.isArray(d.history) ? d.history.filter(y => y.revenue_m != null || y.ebitda_m != null) : [];
  const rows = [['Umsatz', d.revenue], ['EBITDA', d.ebitda], ['Weitere Kennzahlen', d.financials_extra]].filter(([, v]) => v);
  const blocks = [['Unternehmensüberblick', d.company_overview], ['Gesellschafterstruktur', d.shareholder_structure], ['Prozess-Übersicht', d.process_overview]].filter(([, v]) => v);
  return (
    <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-2.5 space-y-1.5">
      <p className="text-[10px] font-mono text-emerald-300 flex items-center gap-1">
        <CheckCircle className="w-3 h-3" /> Standard-Format bereit — geht in die AC-Mail
        <span className="text-muted-foreground ml-auto">{teaser.doc_label} · {fmtD(teaser.analyzed_at)}</span>
      </p>
      {(deal?.broker_name || deal?.status_text) && (
        <p className="text-xs text-amber-200 bg-amber-500/10 border border-amber-500/25 rounded-sm px-2 py-1"
          data-testid={`ac-teaser-broker-${deal?.project_id || ''}`}>
          {deal?.broker_name ? <><span className="font-semibold">Broker:</span> {deal.broker_name}</> : null}
          {deal?.broker_name && deal?.status_text ? ' · ' : ''}
          {deal?.status_text ? <><span className="font-semibold">Prozess-Stand:</span> {deal.status_text}</> : null}
        </p>
      )}
      {hist.length > 0 && (
        <table className="text-[11px] w-full font-mono" data-testid={`ac-teaser-history-${deal?.project_id || ''}`}><tbody>
          <tr className="text-muted-foreground">
            <td className="pr-2 py-0.5"></td>
            {hist.map((y, i) => <td key={i} className="pr-2 py-0.5">{y.year}</td>)}
          </tr>
          <tr><td className="pr-2 py-0.5 text-muted-foreground">Umsatz €m</td>
            {hist.map((y, i) => <td key={i} className="pr-2 py-0.5 text-foreground">{y.revenue_m ?? '—'}</td>)}</tr>
          <tr><td className="pr-2 py-0.5 text-muted-foreground">EBITDA €m</td>
            {hist.map((y, i) => <td key={i} className="pr-2 py-0.5 text-foreground">{y.ebitda_m ?? '—'}</td>)}</tr>
        </tbody></table>
      )}
      {hist.length >= 2 && (
        <p className="text-[10px] text-muted-foreground">In der AC-Mail als Balken-Diagramm mit Marge-Linie.</p>
      )}
      {rows.length > 0 && (
        <table className="text-xs w-full"><tbody>
          {rows.map(([l, v]) => (
            <tr key={l}><td className="text-muted-foreground pr-2 py-0.5 align-top whitespace-nowrap">{l}</td>
              <td className="text-foreground py-0.5">{v}</td></tr>
          ))}
        </tbody></table>
      )}
      {blocks.map(([l, v]) => (
        <div key={l}>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</p>
          <p className="text-xs text-foreground/90 leading-relaxed">{v}</p>
        </div>
      ))}
    </div>
  );
}

function DealPrep({ deal, onDone }) {
  const [sel, setSel] = useState('');
  const [busy, setBusy] = useState(false);
  const [rescreen, setRescreen] = useState(false);

  const options = [
    ...deal.documents.map(doc => ({ key: `doc:${doc.id}`, label: `${doc.label} — ${doc.filename || 'Dokument'}`, disabled: !doc.has_text })),
    ...deal.mail_attachments.map(a => ({ key: `mail:${a.proposal_id}:${a.attachment_id}`, label: `Aus Broker-Mail: ${a.name} (${a.from_name})`, mail: true })),
  ];

  const screen = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      const [kind, p1, p2] = sel.split(':');
      const payload = kind === 'doc' ? { doc_id: p1 } : { proposal_id: p1, attachment_id: p2 };
      await api.screenAcTeaser(deal.project_id, payload);
      toast.success(`${deal.project_name}: Teaser gescreent — Standard-Format ist in der AC-Mail`);
      setRescreen(false);
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Screening fehlgeschlagen'); }
    setBusy(false);
  };

  const showPicker = !deal.teaser || rescreen;
  return (
    <div className="px-4 py-3 border-b border-border last:border-b-0" data-testid={`ac-brokered-prep-${deal.project_id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-medium text-foreground">{deal.project_name}</p>
        <span className="text-[9px] font-mono text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5">BROKERED</span>
        <span className="text-[10px] text-muted-foreground">{deal.broker_name ? `Broker: ${deal.broker_name} · ` : ''}{deal.status_text}</span>
        {deal.teaser && !rescreen && (
          <button onClick={() => setRescreen(true)} data-testid={`ac-teaser-rescreen-${deal.project_id}`}
            className="ml-auto text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Neu screenen
          </button>
        )}
      </div>
      <div className="mt-2 space-y-2">
        {deal.teaser && <TeaserSummary teaser={deal.teaser} deal={deal} />}
        <ManualBroker deal={deal} onSaved={onDone} />
        <ManualFinancials deal={deal} onSaved={onDone} />
        {showPicker && (
          <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-2.5 space-y-2" data-testid={`ac-teaser-picker-${deal.project_id}`}>
            <p className="text-xs text-amber-300">
              Welche Datei ist der Teaser / das CIM? Die KI liest sie und baut das Standard-Format für die AC-Mail
              (Financials, Unternehmensüberblick, Gesellschafter, Prozess).
            </p>
            {options.length === 0 ? (
              <p className="text-xs text-muted-foreground">Keine Dokumente oder Mail-Anhänge gefunden — bitte den Teaser auf der Deal-Seite hochladen.</p>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                <select value={sel} onChange={e => setSel(e.target.value)} data-testid={`ac-teaser-select-${deal.project_id}`}
                  className="flex-1 min-w-[240px] h-8 bg-secondary border border-border rounded-sm px-2 text-xs text-foreground focus:outline-none focus:border-amber-500">
                  <option value="">— Datei wählen —</option>
                  {options.map(o => (
                    <option key={o.key} value={o.key} disabled={o.disabled}>
                      {o.mail ? '📎 ' : ''}{o.label}{o.disabled ? ' (kein Text extrahiert)' : ''}
                    </option>
                  ))}
                </select>
                <button onClick={screen} disabled={busy || !sel} data-testid={`ac-teaser-screen-btn-${deal.project_id}`}
                  className="h-8 px-3 text-xs text-amber-200 border border-amber-500/40 hover:bg-amber-500/10 rounded-sm transition-colors flex items-center gap-1.5 disabled:opacity-40">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {busy ? 'KI liest…' : 'Mit KI screenen'}
                </button>
              </div>
            )}
            {options.some(o => o.mail) && (
              <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                <Mail className="w-3 h-3" /> Mail-Anhänge werden beim Screenen automatisch als Dokument am Deal gespeichert.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export const ACBrokeredPrepCard = ({ refreshKey }) => {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.getAcBrokeredPrep().then(r => setData(r.data)).catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (!data || !(data.deals || []).length) return null;
  const missing = data.deals.filter(d => !d.teaser).length;

  return (
    <div className="bg-card border border-amber-500/25 rounded-sm" data-testid="ac-brokered-prep-card">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2 flex-wrap">
        <FileSearch className="w-4 h-4 text-amber-400" />
        <p className="font-heading text-base text-foreground">Brokered-Deals — Teaser fürs AC vorbereiten</p>
        <span className="text-[10px] font-mono text-muted-foreground ml-auto" data-testid="ac-brokered-prep-count">
          {missing > 0 ? `${missing} von ${data.deals.length} ohne Standard-Format` : 'alle bereit ✓'}
        </span>
      </div>
      <p className="px-4 py-2 text-[11px] text-muted-foreground border-b border-border flex items-center gap-1.5">
        <FileText className="w-3 h-3 shrink-0" />
        Bestätige pro Deal, welche Datei der Teaser/CIM ist — die KI extrahiert das Standard-Format (Financials, Overview, Gesellschafter, Prozess) für die AC-Vorab-Mail.
      </p>
      {data.deals.map(d => <DealPrep key={d.project_id} deal={d} onDone={load} />)}
    </div>
  );
};
